<template>
  <b-modal ref="modal" lazy size="lg" class="text-left">
    <template slot="modal-header">
      <h5 class="modal-title">Job Offer #{{ jobOffer.id }}</h5>
      <div class="pull-right">
        <b-btn variant="default" @click="$refs.modal.hide(true)">
          Cancel
        </b-btn>
        <b-btn variant="info" @click.prevent="save">
          <template v-if="!saveLoading">Save</template>
          <template v-else>
            <b-spinner small />
            <span>Saving...</span>
          </template>
        </b-btn>
      </div>
    </template>

    <b-form>
      <b-form-group
        horizontal
        label="Job Title"
        :invalid-feedback="requiredInvalidFeedback"
        :state="validateState('title')"
      >
        <b-form-input
          type="text"
          v-model="$v.jobOffer.title.$model"
          :state="validateState('title')"
        />
      </b-form-group>

      <b-form-group
        horizontal
        label="Specialty:"
        :invalid-feedback="requiredInvalidFeedback"
        :state="validateState('specialty_id')"
      >
        <b-form-select
          v-model="$v.jobOffer.specialty_id.$model"
          :options="specialtyOptions"
          :state="validateState('specialty_id')"
        />
      </b-form-group>

      <b-form-group
        horizontal
        label="Subspecialty:"
        :invalid-feedback="requiredInvalidFeedback"
        :state="validateState('subspecialty_id')"
      >
        <b-form-select
          v-model="$v.jobOffer.subspecialty_id.$model"
          :options="subspecialtyOptions"
          :state="validateState('subspecialty_id')"
        />
      </b-form-group>

      <b-form-group
        horizontal
        label="Status:"
        :invalid-feedback="requiredInvalidFeedback"
        :state="validateState('status')"
      >
        <b-form-select
          v-model="$v.jobOffer.status.$model"
          :options="statusOptions"
          :state="validateState('status')"
        />
      </b-form-group>

      <b-form-group
        horizontal
        label="Description:"
        :invalid-feedback="requiredInvalidFeedback"
        :state="validateState('description')"
      >
        <b-form-textarea
          v-model="$v.jobOffer.description.$model"
          :state="validateState('description')"
        />
      </b-form-group>

      <b-form-group
        horizontal
        label="Welcome message:"
        :invalid-feedback="requiredInvalidFeedback"
        :state="validateState('welcome_message')"
      >
        <b-form-textarea
          v-model="$v.jobOffer.welcome_message.$model"
          :state="validateState('welcome_message')"
        />
      </b-form-group>

      <b-form-group
        horizontal
        label="PO or Tracking #"
        :invalid-feedback="requiredInvalidFeedback"
        :state="validateState('tracking_num')"
      >
        <b-form-input
          type="text"
          v-model="$v.jobOffer.tracking_num.$model"
          :state="validateState('tracking_num')"
        />
      </b-form-group>

      <b-form-group
        horizontal
        label="Job board URL"
        :invalid-feedback="requiredInvalidFeedback"
        :state="validateState('external_url')"
      >
        <b-form-input
          type="text"
          v-model="$v.jobOffer.external_url.$model"
          :state="validateState('external_url')"
        />
      </b-form-group>

      <b-form-group
        horizontal
        label="Company"
        :invalid-feedback="requiredInvalidFeedback"
        :state="validateState('company_name')"
      >
        <b-form-input
          type="text"
          v-model="$v.jobOffer.company_name.$model"
          :state="validateState('company_name')"
        />
      </b-form-group>

      <b-form-group
        horizontal
        label="Job Location Zip Code"
        :invalid-feedback="requiredInvalidFeedback"
        :state="validateState('location_zip_code')"
      >
        <b-form-input
          type="text"
          v-model="$v.jobOffer.location_zip_code.$model"
          :state="validateState('location_zip_code')"
        />
      </b-form-group>
    </b-form>
    <template slot="modal-footer">
      <div class="d-flex justify-content-end">
        <b-btn v-if="isPosted" variant="danger" @click.prevent="unpost">
          <template v-if="!unpostLoading">Unpost</template>
          <template v-else>
            <b-spinner small />
            <span>Unposting...</span>
          </template>
        </b-btn>
        <b-btn class="ml-3" variant="info" @click="goToJobBuilder(jobOffer.id)">
          Go to Job Builder
        </b-btn>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { validationMixin } from 'vuelidate';
import {
  maxLength,
  minLength,
  numeric,
  required,
  url,
} from 'vuelidate/lib/validators';
import Api from '@/providers/Api';
import { showValidationErrorsAsToasted } from '@/helpers/ToastedHelper';
import { JOB_OFFER_STATUSES, JOB_OFFERS_STATUSES_OPTIONS } from '@/store';

export default {
  name: 'ModalEditForm',
  mixins: [validationMixin],
  props: {
    initialJobOffer: {
      validator: (prop) => typeof prop === 'object' || prop === null,
      default: null,
    },
  },
  data: function () {
    const jobOffer = { ...this.initialJobOffer };
    return {
      saveLoading: false,
      unpostLoading: false,
      jobOfferStatus: this.initialJobOffer
        ? this.initialJobOffer.status
        : JOB_OFFER_STATUSES.DRAFT,
      jobOffer: {
        id: this.initialJobOffer ? jobOffer.id : null,
        title: this.initialJobOffer ? jobOffer.title : null,
        status: this.initialJobOffer ? jobOffer.status : null,
        description: this.initialJobOffer ? jobOffer.description : null,
        welcome_message: this.initialJobOffer ? jobOffer.welcome_message : null,
        tracking_num: this.initialJobOffer ? jobOffer.tracking_num : null,
        external_url: this.initialJobOffer ? jobOffer.external_url : null,
        company_name: this.initialJobOffer ? jobOffer.company_name : null,
        location_zip_code: this.initialJobOffer
          ? jobOffer.location_zip_code
          : null,
        specialty_id: this.initialJobOffer ? jobOffer.specialty_id : null,
        subspecialty_id: this.initialJobOffer ? jobOffer.subspecialty_id : null,
      },
    };
  },
  validations: {
    jobOffer: {
      title: {
        required,
      },
      status: {
        required,
      },
      description: {
        required,
      },
      welcome_message: {
        required,
      },
      tracking_num: {},
      external_url: {
        required,
        url,
      },
      company_name: {
        required,
      },
      location_zip_code: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(5),
        numeric,
      },
      specialty_id: {
        required,
      },
      subspecialty_id: {
        required,
      },
    },
  },
  mounted: function () {
    this.showModal();
    this.$root.$on('bv::modal::hide', this.onModalHide);
    this.$root.$on('bv::modal::hidden', this.onModalHidden);
  },
  beforeDestroy: function () {
    this.$root.$off('bv::modal::hide');
  },
  computed: {
    requiredInvalidFeedback: function () {
      return 'This field is required';
    },
    zipInvalidFeedback: function () {
      if (!this.$v.jobOffer.location_zip_code.numeric) {
        return 'Must contain only numbers';
      }
      if (
        !this.$v.jobOffer.location_zip_code.minLength ||
        !this.$v.jobOffer.location_zip_code.maxLength
      ) {
        return `Zip code should be ${this.$v.jobOffer.location_zip_code.$params.minLength.min} characters long`;
      }
      return 'This field is required';
    },
    jobBoardUrlInvalidFeedback: function () {
      if (!this.$v.jobOffer.external_url.url) {
        return 'Job board URL should be a valid url';
      }
      return 'This field is required';
    },
    statusOptions: function () {
      return JOB_OFFERS_STATUSES_OPTIONS;
    },
    specialtyOptions: function () {
      return this.$store.state.specialtyOptions.filter(
        ({ specialty = null }) =>
          specialty && parseInt(specialty.societiesCount) > 0
      );
    },
    subspecialtyOptions: function () {
      return this.$store.state.subspecialtyOptions.filter(
        (subs) =>
          (subs.subspecialty || {}).specialty_id === this.jobOffer.specialty_id
      );
    },
    isPosted: function () {
      return this.jobOfferStatus === JOB_OFFER_STATUSES.ACTIVE;
    },
  },
  methods: {
    validateState: function (name) {
      const { $dirty, $error } = this.$v.jobOffer[name];
      return $dirty ? !$error : null;
    },
    showModal: function () {
      this.$refs.modal.show();
    },
    save: async function () {
      this.$v.$touch();

      if (this.$v.$anyError) {
        return;
      }

      this.saveLoading = true;

      try {
        const jobOffer = await Api.updateJobOfferDataByGmAdmin(this.jobOffer);
        this.$toasted.success('Job Offer successfully updated.', {
          duration: 3000,
        });
        this.jobOfferStatus = jobOffer.status;
        this.$emit('job-offer-saved', jobOffer);
      } catch (e) {
        this.showErrors(e);
      } finally {
        this.saveLoading = false;
      }
    },
    showErrors: function (error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        showValidationErrorsAsToasted(
          this.$toasted,
          error.response.data.errors
        );
      } else {
        this.$toasted.error(error.response.data.message, { duration: 3000 });
      }
    },
    onModalHide: function (bvModalEvent) {
      if (this.saveLoading) {
        bvModalEvent.preventDefault();
      }
    },
    onModalHidden: function () {
      this.$emit('modal-cancel');
    },
    goToJobBuilder: function (id) {
      this.$router.push(`/recruiter/edit-job-offer/${id}`);
    },
    unpost: async function () {
      this.unpostLoading = true;
      try {
        const jobOffer = await Api.unpostJobOffer(this.jobOffer.id);
        this.$toasted.success('Job Offer successfully unposted.', {
          duration: 3000,
        });
        this.$set(this.jobOffer, 'status', jobOffer.status);
        this.jobOfferStatus = jobOffer.status;
        this.$emit('job-offer-saved', jobOffer);
      } catch (e) {
        this.showErrors(e);
      } finally {
        this.unpostLoading = false;
      }
    },
  },
  watch: {
    'jobOffer.specialty_id': function () {
      this.jobOffer.subspecialty_id = null;
    },
  },
};
</script>

<style scoped />
